import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, ArrowRight } from 'lucide-react';

const STEPS = [
  { field: 'client', label: 'Client', value: '123 Events' },
  { field: 'amount', label: 'Amount', value: 500 },
  { field: 'date', label: 'Due Date', value: '15 April 2026' },
  { field: 'terms', label: 'Terms', value: 'Late payments will incur a 5% fee per week' },
  { field: 'paid', label: '', value: '' },
];

function AnimatedNumber({ target, duration = 1200 }) {
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    let start = 0;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [target, duration]);

  return <span>£{current.toLocaleString()}</span>;
}

export default function InvoiceAnimCard() {
  const [step, setStep] = useState(-1);
  const [showPaid, setShowPaid] = useState(false);
  const [paidBounce, setPaidBounce] = useState(false);
  const timeoutRef = useRef([]);

  const resetAnimation = () => {
    timeoutRef.current.forEach(t => clearTimeout(t));
    timeoutRef.current = [];
    setStep(-1);
    setShowPaid(false);
    setPaidBounce(false);

    const delays = [600, 1400, 2200, 3200, 4400];
    delays.forEach((d, i) => {
      const t = setTimeout(() => {
        if (i < 4) {
          setStep(i);
        } else {
          setShowPaid(true);
          setTimeout(() => setPaidBounce(true), 50);
        }
      }, d);
      timeoutRef.current.push(t);
    });

    const restart = setTimeout(resetAnimation, 7000);
    timeoutRef.current.push(restart);
  };

  useEffect(() => {
    resetAnimation();
    return () => timeoutRef.current.forEach(t => clearTimeout(t));
  }, []);

  return (
    <Link to="/invoice-creator" className="block" data-testid="feature-card-invoices">
      <div className="h-full bg-[#0c0c12] border border-white/[0.06] rounded-xl p-5 sm:p-6 transition-all duration-300 hover:border-[#FF4500]/30 hover:shadow-[0_0_30px_-12px_rgba(255,69,0,0.15)] flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-[#FF4500]/10 flex items-center justify-center shrink-0">
            <Receipt className="w-4 h-4 text-[#FF4500]" />
          </div>
          <h3 className="font-clash text-lg font-medium text-white">Invoice Creation</h3>
        </div>

        {/* Invoice preview */}
        <div className="bg-black/40 border border-white/[0.06] rounded-lg p-4 flex-1 relative overflow-hidden">
          {/* Invoice header */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider">Invoice</p>
              <p className="text-[11px] text-white/60 font-mono">#INV-0042</p>
            </div>
            <div className="w-8 h-8 rounded bg-[#FF4500]/10 flex items-center justify-center">
              <Receipt className="w-3.5 h-3.5 text-[#FF4500]/50" />
            </div>
          </div>

          {/* Fields appear step by step */}
          <div className="space-y-2.5">
            {/* Client */}
            <div className={`transition-all duration-500 ${step >= 0 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'}`}>
              <p className="text-[9px] text-white/30 uppercase tracking-wider">Client</p>
              <p className="text-[12px] text-white/80 font-medium">123 Events</p>
            </div>

            {/* Amount */}
            <div className={`transition-all duration-500 ${step >= 1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'}`}>
              <p className="text-[9px] text-white/30 uppercase tracking-wider">Amount Due</p>
              <p className="text-xl font-bold text-white">
                {step >= 1 ? <AnimatedNumber target={500} /> : '£0'}
              </p>
            </div>

            {/* Due Date */}
            <div className={`transition-all duration-500 ${step >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
              <p className="text-[9px] text-white/30 uppercase tracking-wider">Due Date</p>
              <p className="text-[11px] text-white/70">15 April 2026</p>
            </div>

            {/* Terms */}
            <div className={`transition-all duration-500 ${step >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
              <p className="text-[9px] text-white/30 uppercase tracking-wider">Payment Terms</p>
              <p className="text-[10px] text-white/50 italic leading-relaxed">Late payments will incur a 5% fee per week</p>
            </div>
          </div>

          {/* PAID stamp */}
          {showPaid && (
            <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${paidBounce ? 'scale-100 opacity-100' : 'scale-150 opacity-0'}`}>
              <div className="border-[3px] border-green-500/80 rounded-lg px-5 py-1.5 -rotate-12 bg-green-500/[0.08] backdrop-blur-[2px]"
                   style={{animation: paidBounce ? 'stamp-bounce 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none'}}>
                <span className="text-green-400 text-2xl font-black tracking-widest uppercase">PAID</span>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-white/30 italic mt-4 mb-3">Get paid on time — not chased later</p>
        <span className="inline-flex items-center text-sm font-medium text-[#FF4500] gap-1.5 group">
          Create Your Invoice <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}
